import type { Campaign, LineOfInquiry } from '../types'

export function hasPersistedLinesOfInquiry(campaign: Campaign): boolean {
  return (
    Array.isArray(campaign.linesOfInquiry) && campaign.linesOfInquiry.length > 0
  )
}

/** Post-launch LOIs use `active` / `up_next`; wizard LOIs stay `draft`. */
export function hasLaunchedLoiLifecycle(
  lois: LineOfInquiry[] | null | undefined,
): boolean {
  if (!Array.isArray(lois) || lois.length === 0) return false
  return lois.some(
    (l) =>
      l.lifecycleStatus === 'active' || l.lifecycleStatus === 'up_next',
  )
}

/** True only after launch — not merely `status: active` on a stale or partial row. */
export function isCampaignLaunched(campaign: Campaign): boolean {
  if (campaign.status === 'completed') return true
  return (
    campaign.status === 'active' && hasLaunchedLoiLifecycle(campaign.linesOfInquiry)
  )
}

/** Team & Launch when step 4 + LOIs are persisted, or status is still on the LOI / draft phase. */
export function canAccessTeamLaunchWizard(campaign: Campaign): boolean {
  if (isCampaignLaunched(campaign)) return false
  if (campaign.wizardStep < 4) return false
  if (hasPersistedLinesOfInquiry(campaign)) return true
  if (
    campaign.status === 'lines_of_inquiry' ||
    campaign.status === 'draft' ||
    campaign.status === 'inactive'
  ) {
    return true
  }
  return false
}

/**
 * When the campaign is not active, the URL to resume the wizard at the step
 * that matches server state. `null` for active campaigns (detail view applies).
 */
export function campaignWizardResumePath(campaign: Campaign): string | null {
  if (isCampaignLaunched(campaign)) return null
  if (campaign.status === 'active' && !hasPersistedLinesOfInquiry(campaign)) {
    return null
  }
  if (campaign.wizardStep >= 4 && canAccessTeamLaunchWizard(campaign)) {
    return `/campaigns/${campaign.id}/team-and-launch`
  }
  if (campaign.status === 'goal_analysis') {
    return `/campaigns/${campaign.id}/goal-analysis`
  }
  if (campaign.status === 'lines_of_inquiry') {
    return `/campaigns/${campaign.id}/lines-of-inquiry`
  }
  return `/campaigns/${campaign.id}/setup`
}

/** Edit / resume: wizard when in progress, otherwise campaign dashboard. */
export function campaignEditHref(campaign: Campaign): string {
  return campaignWizardResumePath(campaign) ?? `/campaigns/${campaign.id}`
}
